import { format, parse } from "date-fns";
import { DateTime } from "luxon";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ProductType, SizeType } from "app/features/Order/types/OrderTypes";
import {
  DaysInterface,
  EntitiesEntity,
  HolidayHoursEntity,
  Hours,
} from "app/features/Location/types/YextLocationResponseType";
import { calculateExtraCost } from "app/shared/utils/OrderHelper";
import { RewardsEntity } from "app/features/Categories/types/RewardsAccountType";
import { RewardsDetails } from "app/features/Rewards/types/RewardsDetails";
import { ExpiryDate } from "app/features/LoyalityAddFunds/types/AddFundsRequest";
import { isNotEmpty } from "app/shared/utils/utilityFunctions";
import getBrandName from "app/shared/utils/BrandHelper";
import Constants, {
 AppEvents, CDN_BASE_URL, DateFormat, GlobalAlertType, PASSWORD_MIN_LENGTH,
 SessionStorageKeys,
 UNAUTHORIZED_CODE,
} from "app/shared/constants/constants";
import { decrypt, getKey } from "app/shared/utils/EncryptionUtility";
import logging from "app/shared/utils/logging";
import { isRewardExpired } from "app/features/RewardsHome/RewardsUtility";
import { hashCardNumber } from "app/shared/utils/ValueEncryption";
import { GlobalAlertConfig } from "app/services/api/commonTypes";

const { brandContainer } = getBrandName();
const getProductSize = (item: ProductType): SizeType => {
  const objectVal: SizeType = {
    name: "",
    id: "",
    productId: "",
    currentPrice: 0,
    available: true,
    defaultOptions: [],
  };
  const sizeValue = sessionStorage.getItem(`${item.omsData.id}size`);
  if (sizeValue) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return JSON.parse(sizeValue);
  }
  if (item.omsData.salesItems.length > 1) {
    const obj = item.omsData.salesItems[0];
    objectVal.id = obj.id;
    objectVal.productId = obj.productId;
    objectVal.currentPrice = obj.currentPrice;
    objectVal.available = obj.available;
    objectVal.name = obj.name;
    return objectVal;
  }
  return objectVal;
};
const getExtraPrice = (s: ProductType) => {
  const val = calculateExtraCost(s.subProducts);
  return val.price;
};

export const calculateTotal = (items: ProductType[]) => items.reduce(
  (ack: number, item) => ack
    + item.amount
    * (item.currentPrice === 0
      ? item.omsData.salesItems[0].currentPrice + getExtraPrice(item)
      : item.currentPrice + getExtraPrice(item)),
  0,
);

const getDay = (d: Date) => {
  const weekday = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  return weekday[d?.getDay()];
};

export interface StoreTimeValue {
  isClosed: boolean;
  start: string;
  end: string;
}

//Injecting the human script tag when Fiserv iFrame is accessed
export const onFiservPaymentLoad = () => {
  // inject hs_pagetype
  (window as Window).hs_pagetype = "checkout";
};

const checkHolidayHours = (holidayHours: HolidayHoursEntity[], inputDate: Date) => {
  const val = {} as StoreTimeValue;
  holidayHours?.forEach((holidayHour) => {
    const { date, openIntervals, isClosed } = holidayHour;
    const covDate = new Date(`${date}T00:00:00`);
    if (covDate?.toDateString() === inputDate?.toDateString()) {
      if (isClosed) {
        val.isClosed = true;
        return;
      }
      if (openIntervals && openIntervals?.length > 0) {
        const [first] = openIntervals;
        val.end = first ? first.end : "";
        val.start = first ? first.start : "";
      }
    }
  });
  return val;
};

export const mapStoreHours = (location: string) => {
  switch (location) {
    case "America/Denver":
      return "MT";
    case "America/Los_Angeles":
      return "PT";
    case "America/New_York":
      return "ET";
    case "America/Chicago":
      return "CT";
    default:
      return "";
  }
};

const dayIntervals = (day: DaysInterface) => {
  const val = {} as StoreTimeValue;
  const { isClosed, openIntervals } = day;
  val.isClosed = isClosed;
  val.start = openIntervals ? openIntervals[0]?.start : "";
  val.end = openIntervals ? openIntervals[0]?.end : "";
  return val;
};

function checkClosedForTheDay(h: Hours, d: Date) {
  const val = {} as StoreTimeValue;
  switch (getDay(d)) {
    case "monday": {
      return dayIntervals(h.monday);
    }
    case "tuesday": {
      return dayIntervals(h.tuesday);
    }
    case "wednesday": {
      return dayIntervals(h.wednesday);
    }
    case "thursday": {
      return dayIntervals(h.thursday);
    }
    case "friday": {
      return dayIntervals(h.friday);
    }
    case "saturday": {
      return dayIntervals(h.saturday);
    }
    case "sunday": {
      return dayIntervals(h.sunday);
    }
    default: {
      return val;
    }
  }
}

export const handleTimeStamp = (storeData: EntitiesEntity, d: Date) => {
  //Check holiday hours
  if (storeData?.hours?.holidayHours && storeData.hours.holidayHours?.length > 0) {
    const val = checkHolidayHours(storeData.hours.holidayHours, d);
    //Check is closed
    if (val?.isClosed) {
      return val;
    }
    // Check for holiday hours
    if (val?.start !== undefined) {
      return val;
    }
  }
  //check for regular hours
  return checkClosedForTheDay(storeData?.hours, d);
};

export default getProductSize;

// return true if valid else false
export const isEmailvalid = (email: string) => {
  // eslint-disable-next-line max-len
  const regex = /^(([^<>()[\].,;:\s@!#$%&'*/=?^{|}~"\\]+(\.[^<>()[\].,;:\s@!#$%&'*/=?^{|}~"\\]+)*)|(".+"))@(([^<>()[\].,;:\s@!#$%&'*/=?^{|}~"\\]+\.)+[^<>()[\].,;:\s@!#$%&'*/=?^{|}~"\\]{2,})$/i;
  return (email && regex.test(email));
};

export const isNumber = (value: string) => {
  const regex = /^[0-9\b]+$/;
  return regex.test(value);
};

export const isPhoneNumber = (value: string) => {
  const regex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;
  return regex.test(value);
};

export const isPhoneValid = (value: string) => (value && isPhoneNumber(value));

export enum PaymentErrorType {
  REQUIRED = 'Required',
  INVALID_NAME = 'Invalid Name',
  INVALID_CVV = 'Invalid CVV',
  INVALID_ZIPCODE = 'Invalid Zipcode',
  INVALID_CITY = 'Invalid City',
  INVALID_EXPIRYDATE = 'Invalid Expirydate',
  INVALID_BILLINGADDRESS1 = 'Invalid Billing Address1',
  INVALID_CARDNUMBER = 'Invalid card number',
}

export const validateCreditCardNumber = (cardNumber: string) => {
  const visaRegEx = /^4[0-9]{12}(?:[0-9]{3})?$/;
  const mastercardRegEx = /^5[1-5][0-9]{14}$/;
  const amexRegEx = /^3[47][0-9]{13}$/;
  const discoverRegEx = /^6011[0-9]{12}$|^65[0-9]{14}$/;

  if (visaRegEx.test(cardNumber)) {
    return 'Visa';
  }
  if (mastercardRegEx.test(cardNumber)) {
    return 'Mastercard';
  }
  if (amexRegEx.test(cardNumber)) {
    return 'American Express';
  }
  if (discoverRegEx.test(cardNumber)) {
    return 'Discover';
  }
  return PaymentErrorType.INVALID_CARDNUMBER;
};

const VALID = "Valid";

export const validateName = (name: string) => {
  if (name) {
    return VALID;
  }
  return PaymentErrorType.INVALID_NAME;
};

export const validateCVV = (cvv: string) => {
  const cvvRegEx = /^[0-9]{3,4}$/;

  if (cvvRegEx.test(cvv)) {
    return VALID;
  }
  return PaymentErrorType.INVALID_CVV;
};

export const validateZipCode = (zipCode: string) => {
  const zipCodeRegEx = /^\d{5}$/;

  if (zipCodeRegEx.test(zipCode)) {
    return VALID;
  }
  return PaymentErrorType.INVALID_ZIPCODE;
};

export const validateCity = (city: string) => {
  const cityRegEx = /^[a-zA-Z][a-zA-Z\s-]+[a-zA-Z]$/;
  if (cityRegEx.test(city)) {
    return VALID;
  }
  return PaymentErrorType.INVALID_CITY;
};

export const validateBillingAddress1 = (address1: string) => {
  const billingAddress1RegEx = /^[#.0-9a-zA-Z\s,-]+$/;
  if (billingAddress1RegEx.test(address1)) {
    return VALID;
  }
  return PaymentErrorType.INVALID_BILLINGADDRESS1;
};

export const validateExpirydate = (expDate: string) => {
  // Check if the format is MM/YY
  if (!/^\d{2}\/\d{2}$/.test(expDate)) {
    return PaymentErrorType.INVALID_EXPIRYDATE;
  }
  // split into MM and YY
  const [month, year] = expDate.split("/").map(Number);

  // month between 01 - 12
  if (month < 1 || month > 12) {
    return PaymentErrorType.INVALID_EXPIRYDATE;
  }

  // Get current month and year
  const today = new Date();
  const curMonth = today.getMonth() + 1; // month are 0-base
  const curYear = today.getFullYear() % 100; // last 2 digit

  // Check if the card is expired
  if (year < curYear || (year === curYear && month < curMonth)) {
    return PaymentErrorType.INVALID_EXPIRYDATE;
  }

  return VALID;
};

/**
 * Check a card expiration by comparing to current date in browser
 * @param expiryDate
 */
export const isExpiredCard = (expiryDate: ExpiryDate) => validateExpirydate(`${expiryDate.month}/${expiryDate.year}`) !== VALID;

export const formatExpiryDate = (expDate: string) => {
  const digitsOnly = expDate.replace(/\D/g, "");
  if (digitsOnly.length >= 3) {
    // Add slash after the second character
    const formattedValue = digitsOnly.replace(/(\d{2})(\d{1,2})/, '$1/$2');
    return formattedValue;
  }
  return digitsOnly;
};

export const formatDOB = (input: string) => {
  if (!input) {
    return input;
  }

  let inputValue = input.replace(/\D/g, '');

  if (inputValue.length > 2 && inputValue.length <= 4) {
    inputValue = `${inputValue.slice(0, 2)}/${inputValue.slice(2)}`;
  } else if (inputValue.length > 4) {
    inputValue = `${inputValue.slice(0, 2)}/${inputValue.slice(2, 4)}/${inputValue.slice(4, 8)}`;
  }
//Below seems unreachable code
  // if (inputValue.length > 10) {
  //   inputValue = inputValue.slice(0, 10);
  // }

  return inputValue;
};

export const calcualteDOB = (input: string) => {
  const date = DateTime.fromFormat(input, 'MM/dd/yyyy');
  const currentDate = DateTime.now();
  const age = currentDate.diff(date, 'years').years;
  return parseInt(age.toString(), 10);
};

// eslint-disable-next-line max-len
export const formatDate = (dateString: string, currentFormat: string, requiredFormat: string): string | null => {
  try {
    // Parse the input date string using the provided currentFormat
    const parsedDate = parse(dateString, currentFormat, new Date());

    // Check if the date is valid
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    // Format the parsed date according to the required format
    return format(parsedDate, requiredFormat);
  } catch (error) {
      logging.debug("Formatting", "Error while formatting Date");
      return null;
  }
};

export const validateDOB = (input: string) => {
  const dobRegex = /^\d{2}\/\d{2}\/\d{4}$/;

  const isValidFromat = dobRegex.test(input);

  if (isValidFromat) {
    const inputdate = parse(input, DateFormat.MM_DD_YYYY, new Date());
    return !Number.isNaN(inputdate.getTime());
  }
  return false;
};

export const maskEmailAddress = (emailAddress: string) => {
  if (emailAddress?.match("@")) {
  const [localPart, domain] = emailAddress.split("@");
  return `${localPart.charAt(0)}******${localPart.charAt(localPart.length - 1)}@${domain}`;
}
return emailAddress;
};

export const capitalizeWord = (str: string) => (str.toLowerCase().split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' '));

export const processRewards = (rewards: RewardsEntity[], rewardsDetailsInfo: RewardsDetails[]) => {
  const res: RewardsDetails[] = [];
  if (rewards?.length >= 1 && rewardsDetailsInfo && rewardsDetailsInfo?.length > 0) {
    rewardsDetailsInfo?.forEach((rewardDetails) => {
      rewards?.forEach((rewardsEntity) => {
        if (rewardDetails?.rewardId?.toString() === rewardsEntity?.code) {
          if (rewardsEntity && rewardsEntity?.expirationDate) {
            // eslint-disable-next-line max-len
            const isInStoreReward = !((isNotEmpty(rewardDetails.ruleId) && isNotEmpty(rewardDetails.ruleValue)));
            const formattedDate = new Date(rewardsEntity.expirationDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
            // Add the period after the abbreviated month
            const finalFormattedDate = formattedDate.replace(/^([A-Za-z]+) /, (match, p1) => `${String(p1)}. `);
            res.push({
              ...rewardDetails,
              expireOn: finalFormattedDate,
              quantity: rewardsEntity?.quantity,
              readyToUse: true,
              isInStoreReward,
            });
          }
        }
      });
    });
    // eslint-disable-next-line max-len
    res.sort((rewardsDetails, rewardsDetailsNext) => new Date(rewardsDetails.expireOn).getTime() - new Date(rewardsDetailsNext.expireOn).getTime());
  }
  return res;
};

export const getApplicableRewardsForCart = (
  applicableRewards: RewardsDetails[],
  pickUpDate: string,
): RewardsDetails | null => {
  if (!applicableRewards?.length) return null;
  return applicableRewards.reduce((applicableReward: RewardsDetails | null, reward) => {
    if (!isRewardExpired(reward, pickUpDate)) {
      if (!applicableReward || reward.priority < applicableReward.priority) {
        return reward;
      }
    }
    return applicableReward;
  }, null);
};

export const FilterRewardForCart = (
  aRewards: RewardsDetails[],
  cItems: ProductType[],
  pickUpDate: string,
): RewardsDetails | undefined => {
  const res: RewardsDetails[] = [];
  cItems?.forEach((cartItem) => {
    aRewards?.forEach((reward) => {
      reward?.menuItems?.forEach((menu) => {
        // Check if the menu ID is present in the cart item
        // and if the reward is not expired for the selected date
        if (menu?.omsIds?.includes(cartItem?.omsData?.id) && !isRewardExpired(reward, pickUpDate)) {
          res.push(reward);
        }
      });
    });
  });
  if (res?.length > 1) {
    const sortedRes = res.sort((a, b) => a.priority - b.priority);
    return sortedRes[0];
  }
  return res[0];
};

export const formatMobileNumber = (phoneNumber: string | undefined) => {
  if (phoneNumber) {
    const ds = phoneNumber?.replace(/\D/g, '');
    if (ds?.length > 10) {
      return ds?.replace(/\D/g, '')?.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+1 ($2) $3-$4');
    }
    return ds?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  return phoneNumber;
};

export const getCardLogInfo = (cardNumber: string) => ({
        last3Digits: cardNumber.slice(-3),
        hashedCardNumber: hashCardNumber(cardNumber),
    });
export const handlePinValidations = (
  pin: string,
  pinMinLength: number,
  pinMaxLength: number,
  ): string => {
  if (!pin || pin === "") {
      return Constants.GIFT_CARD_PIN_ERR;
  }

  const invalidPinMsg = Constants.GIFT_CARD_WRONG_PIN_ERR;

  if (!/^\d+$/.test(pin)) {
      return invalidPinMsg;
  }

  if (pin.length < pinMinLength || pin.length > pinMaxLength) {
      return invalidPinMsg;
  }

  return "";
};
export const handleCardNumberValidations = (
   cardNumber: string,
   supportedDigits: number [] = [],
): string => {
  const isInvalid = !cardNumber
         || !supportedDigits.includes(cardNumber.replaceAll("-", "").length);

  return isInvalid ? Constants.GIFT_CARD_NUMBER_ERR : "";
};

export const formatGiftCardPin = (number: string | undefined) => number?.replace(/\D/g, ''); // Remove all non-digit characters

export const formatGiftCardNumber = (
  number: string | undefined,
  allowedLengths: number[] = [],
): string | undefined => {
  if (!number) {
    return number; // Return undefined or null if the input is undefined or null
  }

const cleaned = number.replace(/\D/g, '');
const maxAllowed = allowedLengths.length ? Math.max(...allowedLengths) : cleaned.length;
const limited = cleaned.slice(0, maxAllowed);

  // Efficiently format into groups of 4
return limited
    .match(/.{1,4}/g) // Splits into chunks of max 4 digits
    ?.join('-') ?? '';
};

export const isValidReceiptVisitCode = (value: string) => {
  const regex = /^[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}-[a-zA-Z0-9]{6}-[a-zA-Z0-9]{6}-[a-zA-Z0-9]{5}$/;
  return regex.test(value?.trim());
};

export const validateOnlyLettersSpace = (value: string) => {
  const regex = /^[a-zA-Z\s`]*$/;
  return regex.test(value);
};

export const getImageSrc = (module: string, imageName: string) => {
  if (!module || !imageName) {
    return '';
  }

  const importedImages = `${CDN_BASE_URL}/${brandContainer}/images/${module}/${imageName}`;
  return importedImages;
};

export const getFBPCookie = () => {
  const cookies = document.cookie;

  const match = cookies.match(/_fbp=([^;]+)/);

  return match ? match[1] : "";
};

export const validatePassword = (password: string | null | undefined) => {
  const passwordValidation = {
      minLength: false,
      hasNumber: false,
      hasUpperCase: false,
  };

  if (password && password !== "") {
      return {
          minLength: password.length >= PASSWORD_MIN_LENGTH,
          hasNumber: /\d/.test(password),
          hasUpperCase: /[A-Z]/.test(password),
      };
  }
  return passwordValidation;
};

// src/utils/passwordValidationHelper.ts
export const calculatePasswordErrorClasses = (
  password: string,
  passwordError: { minLength: boolean, hasNumber: boolean, hasUpperCase: boolean },
  passwordErrorMessage: string,
) => {
  const hasPasswordError = !!password && password !== "" && (!passwordError.hasNumber || !passwordError.minLength || !passwordError.hasUpperCase);

  let passwordRuleClass = 'mutedText';
  let minLengthPasswordRuleClass = 'mutedText';
  let hasNumberPasswordRuleClass = 'mutedText';
  let hasUpperCasePasswordRuleClass = 'mutedText';

  if (passwordErrorMessage === 'Invalid') {
    passwordRuleClass = hasPasswordError ? 'errorImage' : 'text-success';
    minLengthPasswordRuleClass = passwordError.minLength ? 'text-success' : 'errorImage';
    hasNumberPasswordRuleClass = passwordError.hasNumber ? 'text-success' : 'errorImage';
    hasUpperCasePasswordRuleClass = passwordError.hasUpperCase ? 'text-success' : 'errorImage';
  } else if (password && password !== "") {
    passwordRuleClass = hasPasswordError ? 'mutedText' : 'text-success';
    minLengthPasswordRuleClass = passwordError.minLength ? 'text-success' : 'mutedText';
    hasNumberPasswordRuleClass = passwordError.hasNumber ? 'text-success' : 'mutedText';
    hasUpperCasePasswordRuleClass = passwordError.hasUpperCase ? 'text-success' : 'mutedText';
  }

  return {
    hasPasswordError,
    passwordRuleClass,
    minLengthPasswordRuleClass,
    hasNumberPasswordRuleClass,
    hasUpperCasePasswordRuleClass,
  };
};
export function decryptAndParse<T>(encryptedString: string): T | null {
  try {
  const tempValue = decrypt(encryptedString);
  return JSON.parse(tempValue.toString()) as T;
  } catch (error) {
    logging.debug(AppEvents.PARSING_ERROR, "Error while decrypting and parsing response");
    return null;
  }
}

export const formatPickupDate = (stringDate: string) => {
  const date = new Date(stringDate);
  return date.toLocaleTimeString('en-US', {
    timeZoneName: 'shortGeneric', month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Utility function to check if an error is a 401 Unauthorized error.
 *
 *  Temporary solution:
 *    - Right now this only checks for 401 errors.
 *    - We need a more structured error-handling framework to cover other
 *      cases (403, 5xx, network issues, etc.).
 *    - This function should eventually be part of a centralized error utils layer
 *      instead of being scattered in components.
 *
 *  Why 401 is handled separately?
 *    - 401 usually means the user session has expired or is invalid.
 *    - In such cases, we want to trigger a logout flow,
 *      not just show a generic error fallback.
 *
 *  Next steps (future improvement):
 *    - Expand this into a more generic error handling.
 *    - Ensure consistency across all RTK Query API calls.
 */
export const isUnauthorizedError = (apiError: unknown): boolean => {
  if (typeof apiError === 'object' && apiError !== null) {
    const err = apiError as FetchBaseQueryError;
    return err.status === UNAUTHORIZED_CODE;
  }
  return false;
};

/**
 * Type guard to check if a value is a valid GlobalAlertConfig.
 *
 * Ensures the object has all required fields (`enabled`, `header`, `message`, `type`)
 * and that `type` is a valid GlobalAlertType.
 *
 * @param obj - The value to validate
 * @returns `true` if valid, otherwise `false`
 */
export const isValidGlobalAlertConfig = (obj: unknown): obj is GlobalAlertConfig => {
  if (typeof obj !== 'object' || obj === null) return false;

  const {
    enabled, header, message, type,
  } = obj as Record<string, unknown>;

  return (
    typeof enabled === 'string'
    && typeof header === 'string'
    && typeof message === 'string'
    && typeof type === 'string'
    && Object.values(GlobalAlertType).includes(type as GlobalAlertType)
  );
};

/**
 * Read persisted scheduleIn and return a safe number (0 fallback).
 */
export const getScheduleIn = (): number => {
    const scheduleInStr = getKey(SessionStorageKeys.SCHEDULE_IN);
    if (scheduleInStr) {
        const scheduleIn = Number(scheduleInStr);
        return Number.isFinite(scheduleIn) ? scheduleIn : 0;
    }
    return 0;
};
